import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { launchActions, launchStore } from "src/modules/engagement/launchStore";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import { useItemRoute } from "src/modules/engagement/workflow/useItemRoute";
import AudienceListView from "./AudienceListView";
import AudienceDetail from "./AudienceDetail";

/*
    Audiences belong to the engagement: a list of them, and one open at a time.
    The open audience is a route segment, so it can be linked and reached with
    back/forward.

    Nothing here depends on a campaign, and nothing is remembered from another
    step. This step used to resolve a "current campaign" and, when an engagement
    had more than one, send the user to the Campaign step to pick — so which
    audiences you saw depended on where you had clicked, and an audience created
    under one campaign vanished when the step later resolved another.
*/
export default function AudienceStep() {
  const { audienceRefreshTrigger, audiences } = useSnapshot(launchStore);
  const { selectedEngagement } = useSnapshot(engagementStore);
  const [openAudienceId, setOpenAudienceId] = useItemRoute();
  const [error, setError] = useState<string | null>(null);

  const engagementId = selectedEngagement?.id ?? 0;
  const organizationId = selectedEngagement?.client?.id ?? 0;

  useEffect(() => {
    if (!engagementId || engagementId < 0) return;
    let cancelled = false;
    setError(null);
    launchStore.isLoadingAudiences = true;
    new AudienceDefinitionService()
      .listAudiences(engagementId)
      .then((loaded) => {
        if (cancelled) return;
        launchActions.setAudiences(loaded);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load audiences.");
      })
      .finally(() => {
        if (cancelled) return;
        launchStore.isLoadingAudiences = false;
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId, audienceRefreshTrigger]);

  const openAudience = audiences.find((a) => a.id === openAudienceId) ?? null;

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {openAudience ? (
        <AudienceDetail
          key={openAudience.id}
          audience={openAudience}
          organizationId={organizationId}
          onBack={() => setOpenAudienceId(null)}
        />
      ) : (
        <AudienceListView engagementId={engagementId} onOpen={setOpenAudienceId} />
      )}
    </>
  );
}
